/**
 * Label Studio Modal wrapper
 *
 * This file provides backward compatibility by wrapping @humansignal/ui Modal
 * with LS-specific providers automatically injected.
 */
import type { ReactElement } from "react";
import {
  modal as coreModal,
  confirm as coreConfirm,
  info as coreInfo,
  type ModalProps,
  type ModalUpdateProps,
  type ExtraProps,
} from "@humansignal/ui/lib/modal";
import { ApiProvider } from "../../providers/ApiProvider";
import { AuthProvider } from "@humansignal/core/providers/AuthProvider";
import { ConfigProvider } from "../../providers/ConfigProvider";
import { ToastProvider } from "@humansignal/ui/lib/toast/toast";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "../../utils/query-client";
import { LocaleProvider } from "@humansignal/core";

export type { ButtonProps as ButtonVariant } from "@humansignal/ui/lib/button/button";

/**
 * Get the default LS providers for modals.
 *
 * ``modal()`` / ``confirm()`` / ``info()`` mount in a fresh React tree
 * (separate from the main app render), so context providers don't
 * propagate automatically — every provider the modal's children need
 * has to be re-injected here. ``LocaleProvider`` is the load-bearing
 * one for translated copy: without it ``useTranslation().t("...")``
 * returns raw keys (``misc.enter_a_name`` instead of ``Enter a name``)
 * inside any imperatively-opened dialog. The provider reads its
 * initial language from localStorage on its own, so no prop is
 * required here — sticky language preference carries through.
 */
const getDefaultProviders = (): ReactElement[] => {
  return [
    <ConfigProvider key="config" />,
    <ToastProvider key="toast" />,
    <ApiProvider key="api" />,
    <AuthProvider key="auth" />,
    <QueryClientProvider key="query" client={queryClient} />,
    <LocaleProvider key="locale" />,
  ];
};

const modalTypes = {
  modal: coreModal,
  confirm: coreConfirm,
  info: coreInfo,
} as const;

const createModal = (type: keyof typeof modalTypes) => {
  return <T,>(props: ModalProps<T> & ExtraProps): ModalUpdateProps<T> => {
    return modalTypes[type]({
      simple: false,
      providers: getDefaultProviders(),
      ...props,
    });
  };
};

// Re-export Modal component and hooks
export const modal = createModal("modal");
export const confirm = createModal("confirm");
export const info = createModal("info");
export { modal as standaloneModal };
export { Modal, useModalControls } from "@humansignal/ui/lib/modal";
